/**
 * Resolver registry for the awaitable signup gate.
 *
 * The 403 SIGNUP_INCOMPLETE interceptor (and any other imperative caller) needs
 * to open the signup funnel and wait for its outcome, but funnel completion is
 * distributed: the terms step finishes it, the guest path finishes it, logout
 * abandons it, closing the sheet dismisses it. This module is the meeting
 * point — stores signal outcomes here, waiters get one shared promise list
 * (mirrors `kycGateStore`'s resolver list: every request gated before
 * completion resumes together).
 */
export type SignupGateOutcome = 'completed' | 'dismissed';

let resolvers: Array<(outcome: SignupGateOutcome) => void> = [];

/** Register a waiter for the in-flight signup funnel's outcome. */
export function waitForSignupGate(): Promise<SignupGateOutcome> {
  return new Promise((resolve) => {
    resolvers.push(resolve);
  });
}

/**
 * Resolve every pending waiter. No-op when none are pending, so completion /
 * close paths call it unconditionally: 'completed' fires first on genuine
 * completion (the funnel closes right after — that later 'dismissed' then hits
 * an empty list).
 */
export function resolveSignupGate(outcome: SignupGateOutcome): void {
  if (resolvers.length === 0) return;
  const pending = resolvers;
  resolvers = [];
  for (const resolve of pending) resolve(outcome);
}
